<script setup lang="ts">
/**
 * Read-only render of one exercise's prompt, math drawn by KaTeX as the player draws it — the
 * Preview tab of the prompt field, a place to proof-read formulas. Nothing here writes.
 */
import type { ImageBlock, TextBlock } from '../../../../../model/assessments/interfaces'
import type { MathExercise } from '../../../../../model/assignments/store/interfaces'

import { AppText } from '@shared/ui/typography'

import ExercisePromptImage from './ExercisePromptImage.vue'
import MathText from './MathText.vue'

defineOptions({
  name: 'ExercisePromptPreview',
})

const props = defineProps<{
  exercise: MathExercise
}>()

/** An empty text block is a prompt not written yet, not a line to draw. */
const isAuthoredBlock = (block: unknown): block is TextBlock | ImageBlock => {
  const candidate = block as Partial<TextBlock | ImageBlock> | null

  if (candidate?.type === 'image') {
    return true
  }

  return candidate?.type === 'text' && typeof candidate.value === 'string' && candidate.value.trim() !== ''
}

const refPrompt = computed<(TextBlock | ImageBlock)[]>(() => {
  const prompt: unknown = props.exercise?.prompt

  return Array.isArray(prompt) ? prompt.filter(isAuthoredBlock) : []
})
</script>

<template>
  <div class="exercise-prompt-preview">
    <AppText
      v-if="refPrompt.length === 0"
      name="body2"
      color="slate-500"
    >
      No prompt yet — a kid would see no question.
    </AppText>
    <template
      v-for="(block, index) in refPrompt"
      :key="index"
    >
      <MathText
        v-if="block.type === 'text'"
        tag="p"
        :value="block.value"
      />
      <ExercisePromptImage
        v-else
        :block="block"
      />
    </template>
  </div>
</template>

<style scoped lang="scss" src="./ExercisePromptPreview.scss" />
